import {
  type HubBodyUpdateBrandingBrandingPut,
  HubBrandingItemId,
  HubFileEncoding,
} from '@robovision/hub'
import { setPrimaryColor } from '@robovision/rvai-base'
import { defineStore } from 'pinia'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import { Branding } from '~domain/branding/Branding'
import { BrandingAssets } from '~domain/branding/BrandingAssets'
import { hubApi } from '~services/api'


const CUSTOM_DARK_MODE_COLOR = '#00b4ff'

export const useBrandingStore = defineStore('branding', () => {
  const state = reactive({
    branding: [] as Branding[],
    publicBranding: [] as BrandingAssets[],
  })

  const getters = {
    getCookiePolicy (): string | undefined {
      return this.getDataUriById(HubBrandingItemId.CookiePolicy, true)
    },
    getDataUriById (itemId: HubBrandingItemId, encodeUri = false): string | undefined {
      if (![
        HubBrandingItemId.CookiePolicy,
        HubBrandingItemId.Favicon,
        HubBrandingItemId.Logo,
        HubBrandingItemId.PrivacyPolicy,
        HubBrandingItemId.TermsAndConditions,
      ].includes(itemId)) return undefined

      const content = state.publicBranding.find(b => b.id === itemId)?.value
      if (!content || typeof content === 'string') return undefined
      return `data:${content.mimeType};${content.encoding === HubFileEncoding.Base64 ? 'base64' : 'utf8'},${encodeUri ? encodeURIComponent(content.data) : content.data}`
    },
    getDocsUrl (): string | undefined {
      const docsUrl = state.publicBranding.find(b => b.id === HubBrandingItemId.DocumentationUrl)?.value
      if (!docsUrl || typeof docsUrl !== 'string') return undefined
      return docsUrl
    },
    getFaviconUri (): string | undefined {
      return this.getDataUriById(HubBrandingItemId.Favicon)
    },
    getInitialFieldsValue (): Record<HubBrandingItemId, null> {
      return state.branding.reduce((acc, curr) => {
        acc[curr.id] = null
        return acc
      }, {} as Record<HubBrandingItemId, null>)
    },
    getLocaleOverride (): string | undefined {
      const content = state.publicBranding.find(b => b.id === HubBrandingItemId.OnScreenText)?.value
      if (!content || typeof content === 'string') return undefined
      return content.data
    },
    getLogoUri (): string | undefined {
      return this.getDataUriById(HubBrandingItemId.Logo)
    },
    getPrimaryColor (): string | undefined {
      const color = state.publicBranding.find(b => b.id === HubBrandingItemId.PrimaryColor)?.value
      if (!color || typeof color !== 'string') return undefined
      return color
    },
    getPrivacyPolicy (): string | undefined {
      return this.getDataUriById(HubBrandingItemId.PrivacyPolicy, true)
    },
    getTermsAndConditions (): string | undefined {
      return this.getDataUriById(HubBrandingItemId.TermsAndConditions, true)
    },
    getTermsAndConditionsPlainContent (): string | undefined {
      const content = state.publicBranding.find(b => b.id === HubBrandingItemId.TermsAndConditions)?.value
      if (!content || typeof content === 'string' || content.encoding !== HubFileEncoding.Plain) return undefined
      return content.data
    },
  }

  const actions = {
    async downloadTermsAndConditions (fileName: string = 'terms-and-conditions.pdf', title?: string): Promise<void> {
      try {
        const response = await hubApi.termsAndConditions.getTermsAndConditions({ title }, { format: 'blob' })
        if (!response) {
          throw new Error('Failed to download terms and conditions')
        }

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = document.createElement('a')
        url.href = window.URL.createObjectURL(blob)
        url.download = fileName
        url.click()
      } catch (e) {
        console.error(e)
        throw e
      }
    },

    async importBranding (file: File): Promise<void> {
      try {
        const { data: result } = await hubApi.branding.importBrandingConfig({ file })

        state.publicBranding = result.map(item => BrandingAssets.fromResponse(item))
      } catch (e) {
        console.error(e)
        throw e
      }
    },

    async loadBranding (): Promise<void> {
      try {
        const { data: result } = await hubApi.branding.getBrandingFields()

        state.branding = result.map(item => Branding.fromResponse(item))
      } catch (e) {
        console.error(e)
        throw e
      }
    },

    async loadPublicBranding (): Promise<void> {
      try {
        const { data: result } = await hubApi.branding.getPublicBranding()

        state.publicBranding = result.map(item => BrandingAssets.fromResponse(item))
      } catch (e) {
        console.error(e)
        throw e
      }
    },

    async resetToDefault (): Promise<void> {
      try {
        await hubApi.branding.resetToDefaults()
        state.publicBranding = []
      } catch (e) {
        console.error(e)
        throw e
      }
    },

    setCustomPrimaryColor (color: string | undefined, isDark: boolean): void {
      if (!color) return
      setPrimaryColor(isDark ? CUSTOM_DARK_MODE_COLOR : color)
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    async updateBranding (data: HubBodyUpdateBrandingBrandingPut): Promise<void> {
      try {
        const { data: result } = await hubApi.branding.updateBranding(data)
        state.publicBranding = result.map(item => BrandingAssets.fromResponse(item))
      } catch (e) {
        console.error(e)
        throw e
      }
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...actions,
    ...getters,
  }
})
